import {
    ColoursObj,
    getContrastYIQ,
    Gutters,
    Icon,
    MainButton,
    Typography,
} from '@eventr/common-storybook'
import { Stack } from '@mui/material'
import { FC, useEffect, useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { DisclosureGroup } from '../common/DisclosureGroup'
import { useGuestStore } from '../feat/guest/guest.store'
import i18n from '../i18n/i18next'
import { bs, useBoundStore } from '../state/bound.store'
import { selectIsItineraryEmpty } from '../state/itineraryState/currentItin.slice'
import { TodoDisclosureGroup } from '../v2/componentCards/todoComponents/todoDisclosureGroup'
import { RendererSplitter } from '../v2/rendererSplitter'
import SkeletoScreen from './components/SkeletonScreen'
import { AddressRow } from './components/carousel/slide/rows/AddressRow'
import { FilesRow } from './components/carousel/slide/rows/FilesRow'
import { WebsiteRow } from './components/carousel/slide/rows/WebsiteRow'
import { ItinVersionRowSummary } from './components/summary/itineraryVersionRow'
import { MarkdownRow } from './components/summary/markdownRow'
import { SyncCalendarRowSummary } from './components/summary/syncCalendarRow'
import AddEmptyItinerarySplash from './team/AddEmptyItinerarySplash'

export const Summary: FC = () => {
    const {
        override,
        noItineraries,
        selectedItinerary,
        eventEnded,
        past,
        todos,
    } = useBoundStore(
        useShallow((s) => {
            return {
                override: s.override,
                noItineraries: s.noItineraries,
                selectedItinerary: s.selectedItinerary,
                eventEnded: s.eventEnded,
                past: s.past,
                todos: s.itineraryComponents.todos,
            }
        })
    )

    const selectedItinEmpty = useBoundStore(selectIsItineraryEmpty)
    const isGuestMode = useGuestStore((s) => s.isGuestMode)

    const generalInfo = bs().getEventInfoV2()?.data
    useEffect(() => {
        if (override !== null) {
            bs().setOverride(null)
        }
    }, [override])

    const generalInfoContent = useMemo(
        () => (
            <Stack
                gap={'8px'}
                style={{
                    background: ColoursObj.backgrounds.secondary,
                    padding: '8px 16px',
                }}
            >
                <AddressRow OSMAddress={generalInfo?.OSMAddress} />
                <WebsiteRow website={generalInfo?.website} />
                <FilesRow files={generalInfo?.additionFiles} />
                <MarkdownRow value={generalInfo?.additionalInfo} />
                {!isGuestMode && <SyncCalendarRowSummary />}
                <ItinVersionRowSummary />
            </Stack>
        ),
        [generalInfo, isGuestMode]
    )

    const content = useMemo(() => {
        return (
            <>
                {!past && <TodoDisclosureGroup open={false} />}
                <RendererSplitter
                    v2Data={selectedItinerary?.v2 || []}
                    remove="event_information"
                />
            </>
        )
    }, [selectedItinerary?.v2, past])

    const isItinEmpty = useMemo(() => {
        return selectedItinEmpty && todos.length === 0
    }, [selectedItinEmpty, todos])

    const generalInfoContentMemo = useMemo(() => {
        return (
            <DisclosureGroup
                headerTextColour={getContrastYIQ(
                    ColoursObj.backgrounds.secondary
                )}
                stickyHeader
                title={i18n.t('general.general')}
                isOpen={true}
                containerStyle={{
                    backgroundColor: ColoursObj.backgrounds.secondary,
                }}
                content={generalInfoContent}
                id="generalInfo"
                icon={
                    <Icon
                        name="Itinerary"
                        bubble
                        bubbleColour={ColoursObj.font.primary}
                    />
                }
            />
        )
    }, [generalInfoContent])

    return (
        <>
            <div style={{ padding: '0px 0px 128px 0px' }}>
                {!generalInfo && !eventEnded ? (
                    <SkeletoScreen />
                ) : (
                    <div>
                        <Gutters topPadding="0px" gutterWidth="0px">
                            <Stack width={'100vw'}>
                                {
                                    <>
                                        {noItineraries ||
                                        (isItinEmpty && !past) ? (
                                            generalInfoContentMemo
                                        ) : (
                                            <>
                                                {past && (
                                                    <>
                                                        <Typography
                                                            variant="bodySubtext"
                                                            style={{
                                                                width: '100%',
                                                                padding:
                                                                    '16px 0px',
                                                                display: 'flex',
                                                                justifyContent:
                                                                    'center',
                                                            }}
                                                            color={
                                                                ColoursObj.core
                                                                    .primary
                                                            }
                                                        >
                                                            {i18n.t(
                                                                'home.viewingPast'
                                                            )}
                                                        </Typography>
                                                        <MainButton
                                                            style={{
                                                                margin: '0px 16px',
                                                            }}
                                                            onClick={() => {
                                                                useBoundStore
                                                                    .getState()
                                                                    .setPast(
                                                                        false
                                                                    )
                                                            }}
                                                        >
                                                            {i18n.t(
                                                                'home.returnPresent'
                                                            )}
                                                        </MainButton>
                                                    </>
                                                )}

                                                {!past &&
                                                    generalInfoContentMemo}
                                                {content}
                                            </>
                                        )}
                                        {isItinEmpty && !past && (
                                            <AddEmptyItinerarySplash />
                                        )}
                                    </>
                                }
                            </Stack>
                        </Gutters>
                    </div>
                )}
            </div>
        </>
    )
}
